import Phaser from 'phaser';
import SocketManager from '../network/SocketManager';
import type { GameResult, RoomState, LeaderboardEntry } from '@shared/types';
import { normalizeRoomCode, setAppRoute } from '../router';
import { playSfx, SFX_TAP_KEY } from '../audio/sfx';
import { API_BASE_URL } from '../config';
import { recordMatchResult } from '../features/FeatureState';
import AuthManager from '../auth/AuthManager';

type LeaderboardUploadConsent = 'unknown' | 'granted' | 'declined';

export class ResultScene extends Phaser.Scene {
  private static readonly PRIVACY_POLICY_URL = 'https://devnova.de/projekte/pickitup/privacy/';
  private static readonly SINGLEPLAYER_SYNC_CONSENT_KEY = 'pickitup_singleplayer_sync_consent';
  private sm!: SocketManager;
  private result!: GameResult;
  private roomState!: RoomState;
  private leaderboardEntries: LeaderboardEntry[] = [];
  private singleplayerRunScore = 0;
  private singleplayerProfileKey: string | null = null;
  private singleplayerLocalBestBefore: number | null = null;
  private singleplayerLocalBestAfter = 0;
  private singleplayerRunIsNewRecord = false;
  private onlineSyncHandler?: () => void;
  private singleplayerSyncConsent: LeaderboardUploadConsent = 'unknown';

  constructor() {
    super({ key: 'ResultScene' });
  }

  init(data: { result: GameResult; roomState: RoomState }): void {
    this.result = data.result;
    this.roomState = data.roomState;
  }

  create(): void {
    this.sm = SocketManager.getInstance();
    const socket = this.sm.getSocket();
    const myId = this.sm.playerId;
    const myRanking = this.resolveMyRanking(myId);
    const isOfflineSingleplayer = this.sm.isLocalMode();
    this.singleplayerRunScore = Math.max(0, Math.round(myRanking?.totalScore ?? 0));
    this.singleplayerSyncConsent = this.readSingleplayerSyncConsent();

    if (isOfflineSingleplayer) {
      this.prepareSingleplayerLocalRecord();
      this.attachOnlineSyncHandler();
    }

    const roomCode = normalizeRoomCode(this.roomState?.roomId);
    if (roomCode) setAppRoute({ kind: 'result', roomId: roomCode }, { replace: true });

    recordMatchResult({
      name: myRanking?.name || this.sm.playerName || 'Spieler',
      score: this.singleplayerRunScore,
      rank: myRanking?.rank ?? 1,
      isWinner: Boolean(myRanking?.isWinner || isOfflineSingleplayer),
      isOffline: isOfflineSingleplayer,
    });

    if (isOfflineSingleplayer) {
      void this.trackChallengeProgress(myRanking, isOfflineSingleplayer);
    }

    this.buildHTML(myId);
    this.attachEvents();

    if (isOfflineSingleplayer) {
      void this.syncSingleplayerRecord();
    } else {
      socket.on('LEADERBOARD', (data: { entries: LeaderboardEntry[] }) => {
        this.leaderboardEntries = data.entries;
        this.renderLeaderboard();
      });
    }

    this.events.once('shutdown', this.cleanup, this);
  }

  private buildHTML(myId: string | undefined): void {
    const uiLayer = document.getElementById('ui-layer');
    if (!uiLayer) return;
    const isOfflineSingleplayer = this.sm.isLocalMode();

    const title = this.resolveResultTitle(myId, isOfflineSingleplayer);

    const rankingsHtml = this.result.rankings.map((player, i) => {
      const isMe = player.id === myId;
      const isW = player.isWinner;
      const medals = ['🥇', '🥈', '🥉', ''];
      const medal = medals[i] ?? '';
      const roundStr = player.roundScores.map((s, ri) => `R${ri + 1}: ${s}`).join(' &nbsp;&nbsp; ');

      return `
        <div class="ui-result-card ${isW ? 'winner' : ''} ${isMe ? 'me' : ''}">
          <div class="ui-result-medal">${medal}</div>
          <div class="ui-result-info">
            <div class="ui-result-name" style="color: ${isMe ? 'var(--accent-gold)' : 'var(--text-main)'}">${player.name}${isMe ? ' (Du)' : ''}</div>
            <div class="ui-result-rounds">${roundStr}</div>
          </div>
          <div class="ui-result-score">${Math.round(player.totalScore).toLocaleString('de-DE')}</div>
        </div>
      `;
    }).join('');

    const leaderboardActionHtml = isOfflineSingleplayer
      ? `
        <div id="singleplayer-record-box" class="ui-record-box">
          <h3>Dein Einzelspieler-Rekord</h3>
          <div class="ui-record-row">
            <span>Diese Runde</span>
            <strong id="sp-current-score" style="color: var(--accent-cyan);">${this.singleplayerRunScore.toLocaleString('de-DE')}</strong>
          </div>
          <div class="ui-record-row">
            <span>Bester Rekord</span>
            <strong id="sp-best-score" style="color: var(--accent-gold);">${this.singleplayerLocalBestAfter.toLocaleString('de-DE')}</strong>
          </div>
          <p id="sp-record-badge" class="ui-status" style="margin: 0 0 8px 0; min-height: 18px; color: ${this.singleplayerRunIsNewRecord ? 'var(--accent-green)' : 'var(--text-dim)'};">${this.singleplayerRunIsNewRecord ? 'Rekord gebrochen!' : 'Knapp! Beim nächsten Mal vielleicht!'}</p>
          <p id="sp-record-status" class="ui-status" style="margin: 0; font-size: 12px;">Lokaler Rekord gespeichert. Synchronisierung wird geprüft...</p>
          <div id="sp-sync-consent" class="ui-record-consent" style="display: ${this.singleplayerSyncConsent === 'granted' ? 'none' : 'block'};">
            <p id="sp-sync-consent-copy" class="ui-record-consent-copy"></p>
            <div class="ui-row ui-record-consent-actions">
              <div class="ui-col">
                <button id="btn-sp-sync-allow" class="ui-btn ui-btn-primary" type="button"></button>
              </div>
              <div class="ui-col">
                <button id="btn-sp-sync-local" class="ui-btn ui-btn-outline" type="button"></button>
              </div>
            </div>
            <button id="btn-sp-sync-privacy" class="ui-btn ui-btn-outline" type="button">Datenschutzerklärung</button>
          </div>
        </div>
      `
      : `
        <div class="ui-lb-submit-box">
          <h3>Top 30? Name eintragen:</h3>
          <div class="ui-row">
            <div class="ui-col" style="flex: 2;">
              <input type="text" id="input-lb-name" class="ui-input" placeholder="Dein Name" maxlength="20" value="${this.escapeHtml(this.sm.playerName)}" />
            </div>
            <div class="ui-col" style="flex: 1;">
              <button id="btn-submit-lb" class="ui-btn ui-btn-primary" style="height: 100%; margin: 0;" disabled>Eintragen</button>
            </div>
          </div>
          <label class="ui-record-consent-toggle" for="input-lb-consent">
            <input id="input-lb-consent" type="checkbox" />
            <span>Ich stimme zu, dass mein Name und mein Score auf den globalen Leaderboard-Server hochgeladen und veröffentlicht werden.</span>
          </label>
          <p class="ui-record-consent-copy" style="margin-bottom: 0;">
            Details stehen in der <a id="link-lb-privacy" class="ui-record-consent-link" href="${ResultScene.PRIVACY_POLICY_URL}" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>.
          </p>
        </div>
      `;

    uiLayer.innerHTML = `
      <div class="ui-bg-glow"></div>
      <div class="ui-bg-glow-2"></div>
      
      <div class="ui-panel" style="width: 640px; max-height: 90vh; overflow-y: auto;">
        <div class="ui-overline">ENDERGEBNIS</div>
        <h1 id="result-title" class="ui-title" style="color: ${title.color}; margin-bottom: var(--sp-xl);">${title.text}</h1>
        
        <div style="width: 100%; margin-bottom: var(--sp-xl);">
          ${rankingsHtml}
        </div>

        ${leaderboardActionHtml}

        <div id="result-leaderboard" style="width: 100%; margin-bottom: var(--sp-lg); display: none;">
          <div class="ui-overline" style="margin-bottom: var(--sp-md);">BESTENLISTE</div>
          <div id="result-leaderboard-list" class="ui-leaderboard-list" style="max-height: 200px; border-radius: var(--radius-sm); background: var(--bg-card); padding: 8px;"></div>
        </div>

        <button id="btn-back-lobby" class="ui-btn ui-btn-outline">ZURÜCK ZUR LOBBY</button>
      </div>
    `;
  }

  private attachEvents(): void {
    const btnSubmit = document.getElementById('btn-submit-lb') as HTMLButtonElement | null;
    if (btnSubmit) {
      const input = document.getElementById('input-lb-name') as HTMLInputElement | null;
      const consent = document.getElementById('input-lb-consent') as HTMLInputElement | null;
      const updateSubmitState = () => {
        const hasName = Boolean((input?.value || '').trim());
        btnSubmit.disabled = !(hasName && consent?.checked);
      };

      input?.addEventListener('input', updateSubmitState);
      consent?.addEventListener('change', updateSubmitState);
      updateSubmitState();

      btnSubmit.addEventListener('click', () => {
        playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
        const name = (input?.value || '').trim();
        if (name && consent?.checked) {
          this.sm.getSocket().emit('SUBMIT_LEADERBOARD', { name });
          btnSubmit.innerText = '✓ OK';
          btnSubmit.className = 'ui-btn ui-btn-success';
          btnSubmit.disabled = true;
        }
      });
    }

    const lbPrivacyLink = document.getElementById('link-lb-privacy');
    lbPrivacyLink?.addEventListener('click', () => {
      playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
    });

    const allowSyncBtn = document.getElementById('btn-sp-sync-allow');
    allowSyncBtn?.addEventListener('click', () => {
      playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
      void this.acceptSingleplayerSyncConsent();
    });

    const localOnlyBtn = document.getElementById('btn-sp-sync-local');
    localOnlyBtn?.addEventListener('click', () => {
      playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
      this.declineSingleplayerSyncConsent();
    });

    const privacyBtn = document.getElementById('btn-sp-sync-privacy');
    privacyBtn?.addEventListener('click', () => {
      playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
      this.openPrivacyPolicy();
    });

    const btnBack = document.getElementById('btn-back-lobby');
    if (btnBack) {
      btnBack.addEventListener('click', () => {
        playSfx(this, SFX_TAP_KEY, { volume: 0.2 });
        this.sm.clearSession();
        setAppRoute({ kind: 'home' });
        this.scene.start('LobbyScene');
      });
    }
  }

  private renderLeaderboard(): void {
    const container = document.getElementById('result-leaderboard');
    const list = document.getElementById('result-leaderboard-list');
    if (!container || !list) return;

    container.style.display = 'block';

    if (this.leaderboardEntries.length === 0) {
      list.innerHTML = '<div class="ui-status">Keine Scores vorhanden</div>';
      return;
    }

    const medals = ['🥇', '🥈', '🥉'];
    list.innerHTML = this.leaderboardEntries.slice(0, 15).map((entry, i) => {
      const isTop3 = i < 3;
      const rankLabel = isTop3 ? medals[i] : `${i + 1}.`;
      return `
        <div class="ui-lb-item">
          <div class="ui-lb-rank">${rankLabel}</div>
          <div class="ui-lb-name">${this.escapeHtml(entry.name)}</div>
          <div class="ui-lb-score">${Math.round(entry.score).toLocaleString('de-DE')}</div>
        </div>
      `;
    }).join('');
  }

  private resolveMyRanking(myId: string | undefined): GameResult['rankings'][number] | undefined {
    if (myId) {
      const mine = this.result.rankings.find((row) => row.id === myId);
      if (mine) return mine;
    }
    return this.result.rankings[0];
  }

  private async syncSingleplayerRecord(): Promise<void> {
    const bestEl = document.getElementById('sp-best-score');
    const badgeEl = document.getElementById('sp-record-badge');
    const statusEl = document.getElementById('sp-record-status');
    if (!bestEl || !badgeEl || !statusEl) return;

    const playerKey = this.singleplayerProfileKey || this.getSingleplayerProfileKey();
    this.singleplayerProfileKey = playerKey;
    if (!playerKey) {
      bestEl.textContent = '-';
      badgeEl.textContent = 'Knapp! Beim nächsten Mal vielleicht!';
      badgeEl.style.color = 'var(--text-dim)';
      statusEl.textContent = 'Rekord-Profil konnte lokal nicht erstellt werden.';
      statusEl.style.color = 'var(--accent-red)';
      return;
    }

    const localBest = this.readSingleplayerLocalBest(playerKey);
    const pendingSync = this.readSingleplayerPendingSync(playerKey);
    const mergedLocalBest = Math.max(
      0,
      this.singleplayerRunScore,
      this.singleplayerLocalBestAfter,
      localBest ?? 0,
      pendingSync ?? 0,
    );
    this.writeSingleplayerLocalBest(playerKey, mergedLocalBest);
    this.singleplayerLocalBestAfter = mergedLocalBest;

    bestEl.textContent = mergedLocalBest.toLocaleString('de-DE');
    badgeEl.textContent = this.singleplayerRunIsNewRecord ? 'Rekord gebrochen!' : 'Knapp! Beim nächsten Mal vielleicht!';
    badgeEl.style.color = this.singleplayerRunIsNewRecord ? 'var(--accent-green)' : 'var(--text-dim)';

    const scoreToSync = Math.max(mergedLocalBest, pendingSync ?? 0);
    if (!this.hasSingleplayerSyncConsent()) {
      this.queueSingleplayerSync(playerKey, scoreToSync);
      this.updateSingleplayerSyncConsentUi();
      statusEl.textContent = this.singleplayerSyncConsent === 'declined'
        ? 'Nur lokal gespeichert. Kein Upload ohne deine Zustimmung.'
        : 'Lokaler Rekord gespeichert. Für den globalen Vergleich ist erst deine Zustimmung nötig.';
      statusEl.style.color = 'var(--text-dim)';
      return;
    }

    if (!this.isOnline()) {
      this.queueSingleplayerSync(playerKey, scoreToSync);
      statusEl.textContent = 'Offline gespeichert. Wird automatisch synchronisiert, sobald Internet verfügbar ist.';
      statusEl.style.color = 'var(--accent-gold)';
      return;
    }

    try {
      statusEl.textContent = 'Synchronisiere mit Server...';
      statusEl.style.color = 'var(--accent-cyan)';

      const response = await fetch(`${API_BASE_URL}/singleplayer/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerKey,
          score: scoreToSync,
        }),
      });
      const data = await response.json().catch(() => ({ success: false, error: 'Ungültige Serverantwort.' })) as {
        success?: boolean;
        error?: string;
        record?: number;
        previousRecord?: number | null;
        isNewRecord?: boolean;
      };
      if (!response.ok || !data.success || typeof data.record !== 'number') {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      const record = Math.max(0, Math.round(data.record));
      const previous = data.previousRecord == null ? null : Math.max(0, Math.round(data.previousRecord));
      const merged = Math.max(record, mergedLocalBest);
      const isNew = Boolean(data.isNewRecord || this.singleplayerRunIsNewRecord);

      this.writeSingleplayerLocalBest(playerKey, merged);
      this.clearSingleplayerPendingSync(playerKey);
      this.singleplayerLocalBestAfter = merged;
      bestEl.textContent = merged.toLocaleString('de-DE');

      if (isNew) {
        const diffBase = previous ?? this.singleplayerLocalBestBefore ?? 0;
        const diff = Math.max(0, this.singleplayerRunScore - diffBase);
        badgeEl.textContent = diff > 0 ? `Rekord gebrochen! +${diff.toLocaleString('de-DE')}` : 'Rekord gebrochen!';
        badgeEl.style.color = 'var(--accent-green)';
      } else {
        badgeEl.textContent = 'Knapp! Beim nächsten Mal vielleicht!';
        badgeEl.style.color = 'var(--text-dim)';
      }

      statusEl.textContent = 'Lokal gespeichert und mit Server synchronisiert.';
      statusEl.style.color = 'var(--accent-cyan)';
    } catch (err) {
      this.queueSingleplayerSync(playerKey, scoreToSync);
      bestEl.textContent = this.singleplayerLocalBestAfter.toLocaleString('de-DE');
      badgeEl.textContent = this.singleplayerRunIsNewRecord ? 'Rekord gebrochen!' : 'Knapp! Beim nächsten Mal vielleicht!';
      badgeEl.style.color = this.singleplayerRunIsNewRecord ? 'var(--accent-green)' : 'var(--text-dim)';
      statusEl.textContent = `Offline gespeichert (${this.normalizeNetworkError(err)}). Auto-Sync bei Internetverbindung aktiv.`;
      statusEl.style.color = 'var(--accent-gold)';
    }
  }

  private prepareSingleplayerLocalRecord(): void {
    const playerKey = this.getSingleplayerProfileKey();
    this.singleplayerProfileKey = playerKey;

    if (!playerKey) {
      this.singleplayerLocalBestBefore = null;
      this.singleplayerLocalBestAfter = this.singleplayerRunScore;
      this.singleplayerRunIsNewRecord = true;
      return;
    }

    const localBest = this.readSingleplayerLocalBest(playerKey);
    this.singleplayerLocalBestBefore = localBest;
    this.singleplayerRunIsNewRecord = localBest == null || this.singleplayerRunScore > localBest;
    this.singleplayerLocalBestAfter = Math.max(this.singleplayerRunScore, localBest ?? 0);
    this.writeSingleplayerLocalBest(playerKey, this.singleplayerLocalBestAfter);

    if (this.singleplayerRunIsNewRecord) {
      this.queueSingleplayerSync(playerKey, this.singleplayerLocalBestAfter);
    }
  }

  private hasSingleplayerSyncConsent(): boolean {
    return this.singleplayerSyncConsent === 'granted';
  }

  private readSingleplayerSyncConsent(): LeaderboardUploadConsent {
    try {
      const raw = localStorage.getItem(ResultScene.SINGLEPLAYER_SYNC_CONSENT_KEY);
      if (raw === 'granted' || raw === 'declined') return raw;
    } catch {
      // ignore local storage failures
    }
    return 'unknown';
  }

  private writeSingleplayerSyncConsent(consent: Exclude<LeaderboardUploadConsent, 'unknown'>): void {
    this.singleplayerSyncConsent = consent;
    try {
      localStorage.setItem(ResultScene.SINGLEPLAYER_SYNC_CONSENT_KEY, consent);
    } catch {
      // ignore local storage failures
    }
  }

  private updateSingleplayerSyncConsentUi(): void {
    const consentBox = document.getElementById('sp-sync-consent');
    const copy = document.getElementById('sp-sync-consent-copy');
    const allowBtn = document.getElementById('btn-sp-sync-allow') as HTMLButtonElement | null;
    const localBtn = document.getElementById('btn-sp-sync-local') as HTMLButtonElement | null;
    const privacyBtn = document.getElementById('btn-sp-sync-privacy') as HTMLButtonElement | null;

    if (!consentBox || !copy || !allowBtn || !localBtn || !privacyBtn) return;

    if (this.singleplayerSyncConsent === 'granted') {
      consentBox.style.display = 'none';
      return;
    }

    consentBox.style.display = 'block';
    if (this.singleplayerSyncConsent === 'declined') {
      copy.textContent = 'Dein Rekord bleibt nur lokal auf diesem Gerät. Wenn du später global vergleichen möchtest, kannst du den Upload hier jederzeit aktivieren.';
      allowBtn.textContent = 'Globalen Vergleich aktivieren';
      localBtn.textContent = 'Nur lokal behalten';
      return;
    }

    copy.textContent = 'Optional: Für den globalen Vergleich werden dein Highscore und eine anonyme Spieler-ID an unseren Server übertragen.';
    allowBtn.textContent = 'Einverstanden';
    localBtn.textContent = 'Nur lokal speichern';
  }

  private async acceptSingleplayerSyncConsent(): Promise<void> {
    this.writeSingleplayerSyncConsent('granted');
    this.updateSingleplayerSyncConsentUi();
    await this.syncSingleplayerRecord();
  }

  private declineSingleplayerSyncConsent(): void {
    this.writeSingleplayerSyncConsent('declined');
    this.updateSingleplayerSyncConsentUi();
    const statusEl = document.getElementById('sp-record-status');
    if (statusEl) {
      statusEl.textContent = 'Nur lokal gespeichert. Kein Upload ohne deine Zustimmung.';
      statusEl.style.color = 'var(--text-dim)';
    }
  }

  private openPrivacyPolicy(): void {
    window.open(ResultScene.PRIVACY_POLICY_URL, '_blank', 'noopener');
  }

  private resolveResultTitle(
    myId: string | undefined,
    isOfflineSingleplayer: boolean,
  ): { text: string; color: string } {
    if (isOfflineSingleplayer) {
      if (this.singleplayerRunIsNewRecord) {
        return { text: 'REKORD GEBROCHEN!', color: 'var(--accent-green)' };
      }
      return { text: 'KNAPP! BEIM NÄCHSTEN MAL VIELLEICHT!', color: 'var(--text-main)' };
    }

    const winner = this.result.rankings[0];
    const isMyWin = winner?.id === myId;
    return {
      text: isMyWin ? 'DU HAST GEWONNEN!' : `${winner?.name} GEWINNT!`,
      color: isMyWin ? 'var(--accent-gold)' : 'var(--text-main)',
    };
  }

  private getSingleplayerProfileKey(): string | null {
    const storageKey = 'pickitup_singleplayer_key';
    try {
      const existing = localStorage.getItem(storageKey);
      if (existing && /^[A-Za-z0-9_-]{16,128}$/.test(existing)) return existing;
      const generated = this.generateSingleplayerProfileKey();
      localStorage.setItem(storageKey, generated);
      return generated;
    } catch {
      return null;
    }
  }

  private getSingleplayerBestStorageKey(playerKey: string): string {
    return `pickitup_singleplayer_best_${playerKey}`;
  }

  private getSingleplayerPendingStorageKey(playerKey: string): string {
    return `pickitup_singleplayer_pending_sync_${playerKey}`;
  }

  private readSingleplayerLocalBest(playerKey: string): number | null {
    try {
      const raw = localStorage.getItem(this.getSingleplayerBestStorageKey(playerKey));
      if (!raw) return null;
      const value = Math.max(0, Math.round(Number(raw)));
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  }

  private writeSingleplayerLocalBest(playerKey: string, score: number): void {
    try {
      localStorage.setItem(this.getSingleplayerBestStorageKey(playerKey), String(Math.max(0, Math.round(score))));
    } catch {
      // ignore local storage failures
    }
  }

  private readSingleplayerPendingSync(playerKey: string): number | null {
    try {
      const raw = localStorage.getItem(this.getSingleplayerPendingStorageKey(playerKey));
      if (!raw) return null;
      const value = Math.max(0, Math.round(Number(raw)));
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  }

  private queueSingleplayerSync(playerKey: string, score: number): void {
    try {
      const safe = Math.max(0, Math.round(score));
      const existing = this.readSingleplayerPendingSync(playerKey) ?? 0;
      localStorage.setItem(this.getSingleplayerPendingStorageKey(playerKey), String(Math.max(existing, safe)));
    } catch {
      // ignore local storage failures
    }
  }

  private clearSingleplayerPendingSync(playerKey: string): void {
    try {
      localStorage.removeItem(this.getSingleplayerPendingStorageKey(playerKey));
    } catch {
      // ignore local storage failures
    }
  }

  private isOnline(): boolean {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine !== false;
  }

  private attachOnlineSyncHandler(): void {
    if (typeof window === 'undefined' || this.onlineSyncHandler) return;
    this.onlineSyncHandler = () => {
      void this.syncSingleplayerRecord();
    };
    window.addEventListener('online', this.onlineSyncHandler);
  }

  private generateSingleplayerProfileKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return `sp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
  }

  private normalizeNetworkError(err: unknown): string {
    const raw = (err instanceof Error ? err.message : String(err || '')).trim();
    if (!raw) return 'Server nicht erreichbar';
    const lower = raw.toLowerCase();
    if (lower.includes('failed to fetch') || lower.includes('network') || lower.includes('load failed')) {
      return 'Server nicht erreichbar';
    }
    return raw;
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private async trackChallengeProgress(
    myRanking: GameResult['rankings'][number] | undefined,
    isOfflineSingleplayer: boolean,
  ): Promise<void> {
    const auth = AuthManager.getInstance();
    if (!auth.isLoggedIn()) return;

    const totalRounds = Array.isArray(myRanking?.roundScores) ? myRanking!.roundScores.length : 0;
    const bestRoundScore = Array.isArray(myRanking?.roundScores)
      ? myRanking!.roundScores.reduce((m, s) => Math.max(m, Math.round(Number(s) || 0)), 0)
      : 0;
    const totalScore = Math.max(0, Math.round(myRanking?.totalScore ?? this.singleplayerRunScore));
    const won = Boolean(myRanking?.isWinner);

    const myState = this.roomState?.players?.find((p) => p.id === this.sm.playerId);
    const comboTypes = myState?.comboScores ? Object.keys(myState.comboScores) : [];
    const perfectRounds = Array.isArray(myRanking?.roundScores)
      ? myRanking!.roundScores.filter((s) => Number(s) >= 30000).length
      : 0;

    const recordBroken = isOfflineSingleplayer && this.singleplayerRunIsNewRecord;
    const fastScore = !isOfflineSingleplayer ? totalScore : 0;

    const payload = {
      scoreDelta: totalScore,
      gameBestScore: Math.max(totalScore, bestRoundScore),
      roundsPlayed: totalRounds,
      gamesPlayed: 1,
      online: !isOfflineSingleplayer,
      won,
      singleplayer: isOfflineSingleplayer,
      recordBroken,
      combosTotal: comboTypes.length,
      comboTypes,
      perfectRounds,
      fastScore,
    };

    try {
      await fetch(`${API_BASE_URL}/challenges/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.getToken()}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.warn('[Challenges] track failed:', err);
    }
  }

  private cleanup(): void {
    const uiLayer = document.getElementById('ui-layer');
    if (uiLayer) uiLayer.innerHTML = '';
    if (!this.sm.isLocalMode()) {
      this.sm.getSocket().off('LEADERBOARD');
    }
    if (this.onlineSyncHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineSyncHandler);
    }
    this.onlineSyncHandler = undefined;
  }
}
